import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Please request a new one.");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) { setError("Passwords don't match."); return; }
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset your password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <BrandLogo size={28} />
            <span className="font-bold text-lg">PassADC</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Set a new password</h1>
          <p className="text-muted-foreground text-sm">
            Pick something you&apos;ll actually remember this time.
          </p>
        </div>

        {done ? (
          <div data-testid="reset-done-card" className="glass rounded-2xl p-6 md:p-8 border border-border text-center space-y-3">
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
            <h2 className="text-lg font-semibold">Password updated</h2>
            <p className="text-sm text-muted-foreground">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-testid="reset-form"
            className="glass rounded-2xl p-6 md:p-8 border border-border space-y-5"
          >
            {error && (
              <div data-testid="reset-error" className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-lg p-3">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="rp-pw" className="block text-sm font-medium mb-2">New password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="rp-pw"
                  data-testid="reset-pw-input"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-card border border-border text-sm focus:outline-none focus:border-primary/40"
                />
              </div>
            </div>

            <div>
              <label htmlFor="rp-pw2" className="block text-sm font-medium mb-2">Confirm password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="rp-pw2"
                  data-testid="reset-pw2-input"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder="Re-enter password"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-card border border-border text-sm focus:outline-none focus:border-primary/40"
                />
              </div>
            </div>

            <Button
              data-testid="reset-submit"
              type="submit"
              disabled={loading || !token}
              className="w-full rounded-full py-6 text-base font-semibold"
            >
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</>) : "Update password"}
            </Button>
          </form>
        )}

        <p className="text-center text-sm mt-6 text-muted-foreground">
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="reset-to-login">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
